import React, {useState, useEffect, useCallback, useContext, useMemo, useRef} from 'react';
import {Button, EditableText, InputGroup, MenuItem, Popover, Tag, Tooltip} from '@blueprintjs/core';
import {Select} from '@blueprintjs/select';
import styled from 'styled-components';
import {DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors} from '@dnd-kit/core';
import {SortableContext, verticalListSortingStrategy, useSortable, arrayMove} from '@dnd-kit/sortable';
import {restrictToVerticalAxis} from '@dnd-kit/modifiers';

import {RoundedInputWrap, CollectionGroupContext} from './shared';
import {EditModeCard, RemoveButton, headingFor, ItemLabel, TitleLabel, EditableTitle, EditableDescription} from './item-card';

const SENSOR_OPTIONS = {activationConstraint: {distance: 5}};
const MODIFIERS = [restrictToVerticalAxis];


const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--sf-vertical-gap);
`;

const DEBOUNCE_MS = 500;

const EditableKey = ({value, disabled, depth, onConfirm}) => {
    const [text, setText] = useState(value);
    const timerRef = useRef(null);
    useEffect(() => setText(value), [value]);
    useEffect(() => () => clearTimeout(timerRef.current), []);
    const handleChange = useCallback((val) => {
        setText(val);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => onConfirm(val), DEBOUNCE_MS);
    }, [onConfirm]);
    const handleConfirm = useCallback((val) => {
        clearTimeout(timerRef.current);
        onConfirm(val);
    }, [onConfirm]);
    return (
        <TitleLabel as={headingFor(depth)}>
            <EditableText
                value={text}
                onChange={handleChange}
                onConfirm={handleConfirm}
                disabled={disabled}
                selectAllOnFocus
            />
        </TitleLabel>
    );
};


const ObjectError = styled.div`
    color: #cd4246;
    font-size: 12px;
    margin-bottom: 4px;
`;

const noLayoutAnimation = () => false;

const SortableEntry = ({id, children}) => {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id, animateLayoutChanges: noLayoutAnimation});
    const style = {
        transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    return (
        <div ref={setNodeRef} style={{...style, outline: 'none'}} {...attributes}>
            {children(listeners, isDragging)}
        </div>
    );
};

const DynamicObjectField = ({value, disabled, readonly, reorderable = true, editMode: editModeProp, initialCollapsed, entrySchema, allowRenaming, propertyNames, fill, depth, itemTitle, itemTitleReadOnly, itemTitleType, itemDescription, itemDescriptionReadOnly, itemDescriptionType, panelWidth, entryErrors, error, renderEntry, onChange}) => {
    const editMode = editModeProp || (initialCollapsed != null ? 'collapsible' : undefined);
    const entries = useMemo(() => value && typeof value === 'object' ? Object.entries(value) : [], [value]);
    const canDrag = reorderable && !disabled && !readonly;
    const entryKeys = useMemo(() => entries.map(([key]) => key), [entries]);

    const sensors = useSensors(
        useSensor(MouseSensor, SENSOR_OPTIONS),
        useSensor(TouchSensor, SENSOR_OPTIONS),
    );

    // Collapse state lifted to survive canDrag branch changes (readonly toggle)
    const [collapsedMap, setCollapsedMap] = useState({});
    const isCollapsed = (key) => key in collapsedMap ? collapsedMap[key] : !!initialCollapsed;
    const toggleCollapse = useCallback((key) => {
        setCollapsedMap(prev => ({
            ...prev,
            [key]: !(key in prev ? prev[key] : !!initialCollapsed)
        }));
    }, [initialCollapsed]);

    const handleDragEnd = useCallback((event) => {
        const {active, over} = event;
        if (!over || active.id === over.id) return;
        const oldIndex = entryKeys.indexOf(String(active.id));
        const newIndex = entryKeys.indexOf(String(over.id));
        if (oldIndex === -1 || newIndex === -1) return;
        const reordered = arrayMove(entries, oldIndex, newIndex);
        onChange(Object.fromEntries(reordered));
    }, [entries, entryKeys, onChange]);

    const handleRename = (oldKey, newKey) => {
        if (!newKey || newKey === oldKey) return;
        if (value[newKey] !== undefined) return;
        const result = {};
        for (const [k, v] of Object.entries(value)) {
            result[k === oldKey ? newKey : k] = v;
        }
        onChange(result);
    };

    const handleEntryChange = (key, newValue) => {
        onChange({...value, [key]: newValue});
    };

    const buildDefaults = useCallback(() => {
        const defaults = {};
        if (entrySchema?.properties) {
            for (const [key, prop] of Object.entries(entrySchema.properties)) {
                if (prop.default !== undefined) defaults[key] = prop.default;
            }
        }
        return defaults;
    }, [entrySchema]);

    const collectionCtx = useContext(CollectionGroupContext);

    // Ref so the header's stale addTag closure always reads the latest value
    const valueRef = useRef(value);
    valueRef.current = value;

    const handleAddWithKey = useCallback((key) => {
        if (editMode === 'collapsible') {
            setCollapsedMap(prev => ({...prev, [key]: false}));
        }
        onChange({...(valueRef.current || {}), [key]: buildDefaults()});
    }, [editMode, onChange, buildDefaults]);

    const availableKeys = propertyNames
        ? propertyNames.filter(k => !value || value[k] === undefined)
        : null;

    const [newKeyText, setNewKeyText] = useState('');
    const [addOpen, setAddOpen] = useState(false);
    const newKeyTrimmed = newKeyText.trim();
    const newKeyExists = newKeyTrimmed && value && value[newKeyTrimmed] !== undefined;

    const handleAddWithText = () => {
        if (!newKeyTrimmed || newKeyExists) return;
        handleAddWithKey(newKeyTrimmed);
        setNewKeyText('');
        setAddOpen(false);
    };

    const handleRemove = (key) => {
        const {[key]: _, ...rest} = value;
        onChange(rest);
    };

    const titleError = (key) => {
        if (!itemTitle || !entryErrors) return undefined;
        const errs = entryErrors(key);
        return errs.find(e => e.instancePath === '/' + itemTitle);
    };

    const descriptionError = (key) => {
        if (!itemDescription || !entryErrors) return undefined;
        const errs = entryErrors(key);
        return errs.find(e => e.instancePath === '/' + itemDescription);
    };

    const renderTitle = (key, entryValue) => {
        if (itemTitle && entryValue && typeof entryValue === 'object') {
            return (
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <EditableTitle
                        value={entryValue[itemTitle]}
                        disabled={disabled || readonly || itemTitleReadOnly}
                        error={titleError(key)}
                        type={itemTitleType}
                        depth={depth}
                        placeholder={`(Edit ${itemTitle})`}
                        onChange={(newVal) => handleEntryChange(key, {...entryValue, [itemTitle]: newVal})}
                    />
                    <Tag minimal round>{key}</Tag>
                </div>
            );
        }
        if (allowRenaming && !disabled && !readonly) {
            return (
                <EditableKey
                    value={key}
                    disabled={false}
                    depth={depth}
                    onConfirm={(newKey) => handleRename(key, newKey)}
                />
            );
        }
        return <ItemLabel as={headingFor(depth)}>{key}</ItemLabel>;
    };

    const entryTitleText = (key, entryValue) =>
        (itemTitle && entryValue?.[itemTitle]) ? String(entryValue[itemTitle]) : key;

    const renderEntryCard = (key, entryValue, dragListeners, isDragging) => (
        <EditModeCard
            editMode={editMode}
            readonly={readonly}
            collapsed={isCollapsed(key)}
            onToggleCollapse={() => toggleCollapse(key)}
            errors={entryErrors ? entryErrors(key) : []}
            panelWidth={panelWidth}
            dragListeners={dragListeners}
            description={itemDescription && entryValue && typeof entryValue === 'object' ? (
                <EditableDescription
                    value={entryValue[itemDescription]}
                    disabled={disabled || readonly || itemDescriptionReadOnly}
                    error={descriptionError(key)}
                    type={itemDescriptionType}
                    depth={depth}
                    placeholder={`(Edit ${itemDescription})`}
                    onChange={(newVal) => handleEntryChange(key, {...entryValue, [itemDescription]: newVal})}
                />
            ) : undefined}
            depth={depth}
            titleText={entryTitleText(key, entryValue)}
            title={renderTitle(key, entryValue)}
            actions={!disabled && !readonly && (
                <Tooltip content={`Remove ${entryTitleText(key, entryValue)}`} placement="top" hoverOpenDelay={400}>
                    <RemoveButton
                        icon="cross"
                        minimal
                        small
                        onClick={() => handleRemove(key)}
                    />
                </Tooltip>
            )}
        >
            {renderEntry(entryValue, key, (newVal) => handleEntryChange(key, newVal))}
        </EditModeCard>
    );

    const addTag = entrySchema && !disabled && !readonly ? (
        availableKeys && !availableKeys.length ? null :
        availableKeys ? (
            <Select
                items={availableKeys}
                itemRenderer={(item, {handleClick, modifiers}) => (
                    <MenuItem
                        key={item}
                        text={item}
                        active={modifiers.active}
                        disabled={modifiers.disabled}
                        onClick={handleClick}
                    />
                )}
                itemPredicate={(query, item) => !query || item.toLowerCase().includes(query.toLowerCase())}
                onItemSelect={handleAddWithKey}
                disabled={!availableKeys.length}
                resetOnClose
                popoverProps={{minimal: true, placement: 'bottom', onOpening: () => collectionCtx?.expand()}}
                filterable
            >
                <Tag icon="plus" round minimal interactive intent="primary">Add</Tag>
            </Select>
        ) : (
            <Popover
                isOpen={addOpen}
                onClose={() => { setAddOpen(false); setNewKeyText(''); }}
                content={
                    <div style={{padding: 8, display: 'flex', gap: 4, alignItems: 'center'}}>
                        <RoundedInputWrap>
                            <InputGroup
                                value={newKeyText}
                                onChange={e => setNewKeyText(e.target.value)}
                                placeholder="Property name"
                                small
                                autoFocus
                                intent={newKeyExists ? 'danger' : 'none'}
                                onKeyDown={e => e.key === 'Enter' && handleAddWithText()}
                            />
                        </RoundedInputWrap>
                        <Button
                            icon="tick"
                            small
                            intent="primary"
                            onClick={handleAddWithText}
                            disabled={!newKeyTrimmed || newKeyExists}
                        />
                    </div>
                }
                placement="bottom"
            >
                <Tag icon="plus" round minimal interactive intent="primary" onClick={() => { collectionCtx?.expand(); setAddOpen(true); }}>Add</Tag>
            </Popover>
        )
    ) : null;

    const headerKey = `${!!error}|${entries.length}|${disabled}|${readonly}|${availableKeys?.length}`;
    useEffect(() => {
        if (collectionCtx) collectionCtx.setHeaderExtra(headerKey, (
            <>
                {error && <ObjectError>{error.message}</ObjectError>}
                <Tag minimal round>{propertyNames ? `${entries.length}/${propertyNames.length}` : `${entries.length}`} items</Tag>
                {addTag}
            </>
        ));
    }, [headerKey]);

    return (
        <Wrapper>
            {canDrag ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={MODIFIERS} onDragEnd={handleDragEnd}>
                    <div>
                        <SortableContext items={entryKeys} strategy={verticalListSortingStrategy}>
                            {entries.map(([key, entryValue]) => (
                                <SortableEntry key={key} id={key}>
                                    {(listeners, isDragging) => renderEntryCard(key, entryValue, listeners, isDragging)}
                                </SortableEntry>
                            ))}
                        </SortableContext>
                    </div>
                </DndContext>
            ) : (
                entries.map(([key, entryValue]) => (
                    <div key={key}>
                        {renderEntryCard(key, entryValue, null)}
                    </div>
                ))
            )}
        </Wrapper>
    );
};

export {DynamicObjectField};
